import * as THREE from 'three';
import { gsap } from 'gsap';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import TextureAnimator from '@/assets/utils/TextureAnimator';
import { disposeHierarchy, disposeNode } from '@/assets/utils/threejsDispose';

const loadingManager = new THREE.LoadingManager();

class AR {
  constructor(markersTemplate, {
    onPlay,
    onStop,
    onLoading,
    onLoad,
  }) {
    this.markers = Marker.fromArray(markersTemplate);
    this.currentMarker = null;
    this.stopAnimationTimer = null;
    this.startAnimationTimer = null;
    this.onPlay = onPlay;
    this.onStop = onStop;
    this.onLoading = onLoading;
    this.onLoad = onLoad;
    this.animationIsPlaying = false;
    this.playersMuted = false;
  }

  setupMarkerRoots(scene, arToolkitContext) {
    this.markers.forEach((marker) => marker.setupMarkerRoot(scene, arToolkitContext));
  }

  initPlayersOnClick() {
    this.markers.forEach((marker) => marker.initAudioPlayer());
  }

  update(deltaTime, debugConsole) {
    // start/stop animation on track/untrack
    if (this.currentMarker === null) {
      for (const marker of this.markers) {
        const animated = this.animate(marker, deltaTime);

        if (animated) {
          this.currentMarker = marker;
          this.markers
            .filter((_marker) => _marker !== marker)
            .forEach((_marker) => this.stopAnimation(_marker));
          break;
        }
      }
      return;
    }

    this.animate(this.currentMarker, deltaTime);
  }

  animate(marker, deltaTime) {
    const markerTracked = marker.markerControls.object3d.visible;
    let animated = false;

    // marker tracked first time
    if (markerTracked && !marker.isPlaying) {
      animated = true;

      // clear other timers
      this.markers
        .filter((_marker) => _marker !== marker)
        .forEach((_marker) => {
          clearTimeout(_marker.startAnimationTimer);
          _marker.startAnimationTimer = null;
        });

      if (marker.startAnimationTimer !== null) return;

      this.startAnimationAfterDelay(marker);
    }

    // marker untracked
    if (!markerTracked && marker.isPlaying) {
      // clear other timers
      this.markers
        .filter((_marker) => _marker !== marker)
        .forEach((_marker) => {
          clearTimeout(_marker.stopAnimationTimer);
          _marker.stopAnimationTimer = null;
        });

      if (marker.stopAnimationTimer !== null) return;

      this.stopAnimationAfterDelay(marker);
    }

    if (markerTracked && marker.isPlaying) {
      clearTimeout(marker.stopAnimationTimer);
      marker.stopAnimationTimer = null;
    }

    marker.markerTracked = markerTracked;

    marker.animations.forEach((animation) => {
      // ELK CONDITION
      if (!animation.update) return;
      animation.update(deltaTime);
    });

    return animated;
  }

  startAnimation(marker, resetAudio = true) {
    // TODO: DOES THIS CYCLE WORK CORRECTLY?
    for (const _marker in this.markers) {
      if (marker.isLoading || marker.isPlaying) return;
    }

    if (!marker.texturesLoaded && !marker.isLoading) {
      this.onLoading();
      marker.loadAnimation(() => {
        this.onLoad();

        if (marker !== this.currentMarker) return;
        if (!marker.markerTracked) return;

        this.startAnimation(...arguments);
      });
      return;
    }

    if (resetAudio) {
      marker.audioPlayer.currentTime = 0;
      marker.audioPlayer.src = marker.audioSrc;
    }

    // play audio
    marker.isPlaying = true;
    marker.audioPlayer.volume = 1;
    marker.audioPlayer.play();

    marker.animations.forEach((animation) => {
      // ELK CONDITION
      if (animation.resume) {
        animation.resume();
        return;
      }
      animation.play();
    });
    marker.rootObjects.forEach((obj) => {
      marker.markerRoot.add(obj);
    });
    this.animationIsPlaying = true;
    this.onPlay();
    marker.onAnimationStart();
  }

  startAnimationAfterDelay(marker) {
    clearTimeout(marker.stopAnimationTimer);
    marker.stopAnimationTimer = null;

    marker.startAnimationTimer = setTimeout(() => {
      marker.startAnimationTimer = null;
      if (marker !== this.currentMarker) return;
      if (!marker.markerTracked) {
        this.currentMarker = null;
        return;
      }

      this.startAnimation(marker);
    }, marker.trackTime);
  }

  stopAnimation(marker) {
    this.onStop();
    this.animationIsPlaying = false;

    marker.audioPlayer.pause();
    marker.animations.forEach((animation) => {
      // CONDITION FOR ELK
      if (animation.pause) {
        animation.pause();
        return;
      }
      // default
      animation.stop()
    });

    marker.isPlaying = false;

    marker.rootObjects.forEach((obj) => {
      const selectedObj = marker.markerRoot.getObjectByName(obj.name);

      // alert(JSON.stringify(obj, null, 2));
      // marker.debug(selectedObj.material);
      // disposeNode(selectedObj);
      // {
      //   const mtrl = selectedObj.material;
      //   if (mtrl.map)               mtrl.map.dispose ();
      //   if (mtrl.lightMap)          mtrl.lightMap.dispose ();
      //   if (mtrl.bumpMap)           mtrl.bumpMap.dispose ();
      //   if (mtrl.normalMap)         mtrl.normalMap.dispose ();
      //   if (mtrl.specularMap)       mtrl.specularMap.dispose ();
      //   if (mtrl.envMap)            mtrl.envMap.dispose ();
      //   if (mtrl.alphaMap)          mtrl.alphaMap.dispose();
      //   if (mtrl.aoMap)             mtrl.aoMap.dispose();
      //   if (mtrl.displacementMap)   mtrl.displacementMap.dispose();
      //   if (mtrl.emissiveMap)       mtrl.emissiveMap.dispose();
      //   if (mtrl.gradientMap)       mtrl.gradientMap.dispose();
      //   if (mtrl.metalnessMap)      mtrl.metalnessMap.dispose();
      //   if (mtrl.roughnessMap)      mtrl.roughnessMap.dispose();
      // }
      selectedObj?.material?.map?.dispose();
      selectedObj?.material?.dispose();

      marker.markerRoot.remove(selectedObj);
      marker.debug();
    });
    marker.onAnimationStop();
  }

  stopAnimationAfterDelay(marker) {
    marker.stopAnimationTimer = setTimeout(() => {
      marker.stopAnimationTimer = null;
      if (marker.markerTracked) return;

      // fade-out audio
      gsap.to(marker.audioPlayer, {
        volume: 0,
        duration: 0.25,
        onComplete: () => {
          if (marker.markerTracked && marker === this.currentMarker) {
            marker.audioPlayer.volume = 1;
            return;
          }
          this.currentMarker = null;
          this.stopAnimation(marker);
        },
      });
    }, marker.detrackTime);
  }

  toggleVolume() {
    this.playersMuted = !this.playersMuted;
    this.markers.forEach((marker) => {
      marker.audioPlayer.muted = this.playersMuted;
    });
  }
}

class Marker {
  constructor(options = {}) {
    const {
      markerControls = null,
      modelSrc = null,
      audioSrc = null,
      patternName = null,
      sprites = [],
      color = 0xffffff,
      onAnimationStart = () => {},
      onAnimationStop = () => {},
      debug = () => {},
      infoText = '',
      name = '',
      htmlId = null,
    } = options;

    this.markerControls = markerControls;
    this.audioSrc = audioSrc;
    this.patternName = patternName;
    this.color = color;
    this.sprites = sprites;
    this.isPlaying = false;
    this.infoText = infoText;
    this.name = name;
    this.htmlId = htmlId;
    this.onAnimationStart = onAnimationStart;
    this.onAnimationStop = onAnimationStop;
    this.debug = debug;

    this.audioPlayer = new Audio();
    this.audioPlayer.addEventListener('ended', function () {
      this.currentTime = 0;
      this.play();
    }, false);

    this.markerTracked = false;
    this.markerRoot = null;
    this.rootObjects = [];
    this.detrackTime = 700;
    this.trackTime = 300;
    this.stopAnimationTimer = null;
    this.startAnimationTimer = null;
    this.animationMixer = null;
    this.animations = [];
    this.isLoading = false;
    this.texturesLoaded = false;
  }

  static fromArray(array) {
    return array.map((obj) => new Marker(obj));
  }

  initAudioPlayer() {
    this.audioPlayer.volume = 0;
    this.audioPlayer.play();
  }

  onProgress(xhr) {
    console.log(`${xhr.loaded / xhr.total * 100}% loaded`);
  }

  onError(xhr) {
    console.log('An error happened');
  }

  addColorPlane() {
    const planeGeometry = new THREE.PlaneBufferGeometry(1, 1, 4, 4);
    const planeMaterial = new THREE.MeshBasicMaterial({ color: this.color, opacity: 0.5 });
    const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
    planeMesh.rotation.x = -Math.PI / 2;

    planeMesh.name = 'planeMesh';
    this.rootObjects.push(planeMesh);
  }

  loadAnimation(cb) {
    this.isLoading = true;
    const loadManager = new THREE.LoadingManager(() => {
      this.texturesLoaded = true;
      this.loadAudio(() => {
        cb();
      });
    });

    this.sprites.forEach((sprite) => {
      //ELK ONLY
      try {
        if (this.htmlId) {
          const animation = this.initSpriteAnimation(
            this.htmlId,
            sprite.numTiles,
            sprite.tilesHoriz,
            sprite.duration,
          );
          this.animations.push(animation);

          this.texturesLoaded = true;
          this.loadAudio(() => {
            cb();
          });
          return;
        }
      } catch (err) {
        this.debug(err);
      }

      new THREE.TextureLoader(loadManager)
        .load(sprite.src, (texture) => {
          texture.minFilter = THREE.LinearMipMapLinearFilter;
          texture.magFilter = THREE.LinearFilter;

          const animation = new TextureAnimator(
            texture,
            sprite.tilesHoriz,
            sprite.tilesVert,
            sprite.numTiles,
            sprite.duration,
            sprite.repeat,
          ); // texture, #horiz, #vert, #total, duration, ?repeat?
          this.animations.push(animation);

          const planeGeometry = new THREE.PlaneBufferGeometry(
            sprite.widthRatio, sprite.heightRatio, 400, 400,
          );
          const planeMaterial = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
          });
          const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
          planeMesh.rotation.x = -Math.PI / 2;
          planeMesh.position.y += sprite.layer * 0.6;
          planeMesh.position.x += sprite.x;
          planeMesh.position.z += sprite.z;

          planeMesh.scale.set(sprite.scale, sprite.scale, sprite.scale);

          planeMesh.name = sprite.name;
          this.rootObjects.push(planeMesh);
        }, this.onProgress, this.onError);
    });
  }

  loadAudio(cb) {
    this.audioPlayer.src = this.audioSrc;
    const onCanPlayThrough = () => {
      this.isLoading = false;
      cb();
      this.audioPlayer.removeEventListener('canplaythrough', onCanPlayThrough);
    };
    this.audioPlayer.addEventListener('canplaythrough', onCanPlayThrough);
    this.audioPlayer.load();
  }

  setupMarkerRoot(scene, arToolkitContext) {
    // build markerControls
    const markerRoot = new THREE.Object3D();

    this.markerRoot = markerRoot;
    scene.add(markerRoot);

    this.markerControls = new THREEx.ArMarkerControls(arToolkitContext, markerRoot, {
      type: 'pattern',
      patternUrl: `data/${this.patternName}.patt`,
      minConfidence: 0.6,
      smooth: true,
      smoothCount: 10,
    });

    // just for visual testing
    // this.addSpritePlanes();
    // this.addColorPlane();
  }

  // eslint-disable-next-line class-methods-use-this
  initSpriteAnimation(elId, frames, col, duration) {
    const el = window.document.querySelector(elId);
    this.debug(el);
    const width = gsap.getProperty(el.parentNode, 'width');
    const height = gsap.getProperty(el.parentNode, 'height');
    const tl = gsap.timeline({ repeat: -1 });

    for (let i = 0; i < frames; i++) {
      tl.set(el, {
        x: (i % col) * -width,
        y: (Math.floor(frames / col) - Math.floor(i / col)) * -height,
      }, i / (frames - 1) * duration);
    }
    return tl;
  }
}

export {
  AR,
  Marker,
  loadingManager,
};
